import React from 'react';
import styled from 'styled-components';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import theme from '../../theme';

const IndustriesSection = styled.section`
  padding: 5rem 0;
  background-color: ${theme.colors.lightGray};
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const SectionTitle = styled.h2`
  margin-bottom: 1rem;
  
  span {
    color: ${theme.colors.primary};
  }
`;

const SectionSubtitle = styled.p`
  font-size: ${theme.fontSizes.medium};
  color: ${theme.colors.darkGray};
  max-width: 700px;
  margin: 0 auto;
`;

const FeaturedIndustry = styled.div`
  margin-bottom: 4rem;
`;

const FeaturedCard = styled(Link)`
  display: block;
  position: relative;
  height: 360px;
  border-radius: ${theme.borderRadius};
  overflow: hidden;
  box-shadow: ${theme.boxShadow};
  transition: ${theme.transition};
  margin-bottom: 1.5rem;
  
  &:hover {
    transform: translateY(-5px);
    
    &:before {
      background-color: rgba(0, 0, 0, 0.5);
    }
  }
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.3);
    transition: ${theme.transition};
    z-index: 1;
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    height: 280px;
  }
`;

const FeaturedImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: ${theme.transition};
`;

const FeaturedContent = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 2rem;
  z-index: 2;
  color: ${theme.colors.white};
`;

const FeaturedTag = styled.div`
  display: inline-block;
  background-color: ${theme.colors.primary};
  color: ${theme.colors.white};
  font-size: ${theme.fontSizes.small};
  font-weight: 600;
  padding: 0.25rem 1rem;
  border-radius: 20px;
  margin-bottom: 1rem;
`;

const FeaturedTitle = styled.h3`
  font-size: ${theme.fontSizes.xlarge};
  margin-bottom: 1rem;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: ${theme.fontSizes.large};
  }
`;

const FeaturedDescription = styled.p`
  margin-bottom: 1.5rem;
  opacity: 0.9;
  font-size: ${theme.fontSizes.medium};
  max-width: 600px;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: ${theme.fontSizes.normal};
  }
`;

const FeaturedButton = styled.span`
  display: inline-flex;
  align-items: center;
  background-color: ${theme.colors.white};
  color: ${theme.colors.primary};
  font-weight: 600;
  padding: 0.75rem 1.5rem;
  border-radius: ${theme.borderRadius};
  transition: ${theme.transition};
  
  &:after {
    content: '→';
    margin-left: 0.5rem;
  }
  
  &:hover {
    background-color: ${theme.colors.primary};
    color: ${theme.colors.white};
  }
`;

const IndustriesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  
  @media (max-width: ${theme.breakpoints.desktop}) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const IndustryCard = styled(Link)`
  display: block;
  position: relative;
  height: 200px;
  border-radius: ${theme.borderRadius};
  overflow: hidden;
  box-shadow: ${theme.boxShadow};
  transition: ${theme.transition};
  
  &:hover {
    transform: translateY(-5px);
    
    &:before {
      background-color: rgba(0, 0, 0, 0.5);
    }
  }
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.3);
    transition: ${theme.transition};
    z-index: 1;
  }
`;

const IndustryImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: ${theme.transition};
`;

const IndustryTitle = styled.h3`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 1.5rem;
  color: ${theme.colors.white};
  font-size: ${theme.fontSizes.medium};
  z-index: 2;
`;

const Industries = () => {
  const { t } = useTranslation();
  const { lang } = useParams();
  
  const industries = [
    {
      titleKey: 'industries.items.automotive',
      image: `${process.env.PUBLIC_URL}/industries-automotive.jpeg`,
      path: `/${lang}/industries/automotive`
    },
    {
      titleKey: 'industries.items.tire',
      image: `${process.env.PUBLIC_URL}/industries-tire.jpeg`,
      path: `/${lang}/industries/tire`
    },
    {
      titleKey: 'industries.items.textile',
      image: `${process.env.PUBLIC_URL}/industries-textile.jpeg`,
      path: `/${lang}/industries/textile`
    },
    {
      titleKey: 'industries.items.construction',
      image: `${process.env.PUBLIC_URL}/industries-construction.jpeg`,
      path: `/${lang}/industries/construction`
    },
    {
      titleKey: 'industries.items.home',
      image: `${process.env.PUBLIC_URL}/industries-home.jpeg`,
      path: `/${lang}/industries/home`
    },
    {
      titleKey: 'industries.items.electronics',
      image: `${process.env.PUBLIC_URL}/industries-electronics.jpeg`,
      path: `/${lang}/industries/electronics`
    },
    {
      titleKey: 'industries.items.publishing',
      image: `${process.env.PUBLIC_URL}/industries-publishing.jpeg`,
      path: `/${lang}/industries/publishing`
    },
    {
      titleKey: 'industries.items.optical',
      image: `${process.env.PUBLIC_URL}/industries-optical.jpeg`,
      path: `/${lang}/industries/optical`
    }
  ];
  
  return (
    <IndustriesSection>
      <div className="container">
        <SectionHeader>
          <SectionTitle dangerouslySetInnerHTML={{ __html: t('industries.title') }} />
          <SectionSubtitle>
            {t('industries.subtitle')}
          </SectionSubtitle>
        </SectionHeader>
        
        <FeaturedIndustry>
          <FeaturedCard to={`/${lang}/industries/food`}>
            <FeaturedImage 
              src={`${process.env.PUBLIC_URL}/food-featured-title-bg.jpeg`} 
              alt={t('industries.featured.title')}
              loading="lazy"
            />
            <FeaturedContent>
              <FeaturedTag>{t('industries.featured.tag')}</FeaturedTag>
              <FeaturedTitle>{t('industries.featured.title')}</FeaturedTitle>
              <FeaturedDescription>
                {t('industries.featured.description')}
              </FeaturedDescription>
              <FeaturedButton>{t('industries.featured.button')}</FeaturedButton>
            </FeaturedContent>
          </FeaturedCard>
        </FeaturedIndustry>
        
        <SectionHeader>
          <SectionTitle dangerouslySetInnerHTML={{ __html: t('industries.otherTitle') }} />
        </SectionHeader>
        
        <IndustriesGrid>
          {industries.map((industry, index) => (
            <IndustryCard key={index} to={industry.path}>
              <IndustryImage src={industry.image} alt={t(industry.titleKey)} loading="lazy" />
              <IndustryTitle>{t(industry.titleKey)}</IndustryTitle>
            </IndustryCard>
          ))}
        </IndustriesGrid>
      </div>
    </IndustriesSection>
  );
};

export default Industries; 